/**
 * App-handtekening voor startverzoeken (23-09-2026, sprint 006 A3).
 *
 * De Windows secure browser zet de X-TW-headers zelf op netwerkniveau. De
 * iPad-app kan dat niet: WKWebView laat fetch-verzoeken van de pagina niet
 * aanvullen. Daarom vraagt de pagina de app om een handtekening voor één
 * verzoek (methode + pad) via de brug `window.twOndertekenVerzoek`, die de app
 * injecteert. De app rekent de HMAC uit; het geheim komt nooit in de pagina.
 *
 * Zonder brug (gewone browser, Windows-app, oude iPad-app) geeft dit {} en
 * verandert er niets aan het verzoek. Een fout of een trage brug mag een
 * start nooit blokkeren: dan ook {} en ziet de server een gewone browser.
 */

const BRUG_TIMEOUT_MS = 1500;

export function kanOndertekenen() {
  return typeof window !== 'undefined' && typeof window.twOndertekenVerzoek === 'function';
}

/**
 * @param {string} methode  HTTP-methode, bijv. 'POST'
 * @param {string} url      volledige URL of pad; alleen het pad telt mee
 * @returns {Promise<Object>} de vier X-TW-headers, of {} zonder brug of bij een fout
 */
export async function attestHeaders(methode, url) {
  if (!kanOndertekenen()) return {};
  let timer = null;
  try {
    const pad = new URL(url, window.location.origin).pathname;
    const verzoek = Promise.resolve(window.twOndertekenVerzoek(String(methode || 'GET').toUpperCase(), pad));
    const tijdslimiet = new Promise((resolve) => { timer = setTimeout(() => resolve(null), BRUG_TIMEOUT_MS); });
    const antwoord = await Promise.race([verzoek, tijdslimiet]);
    if (!antwoord || !antwoord.client || !antwoord.timestamp || !antwoord.nonce || !antwoord.attest) return {};
    return {
      'X-TW-Client': String(antwoord.client),
      'X-TW-Timestamp': String(antwoord.timestamp),
      'X-TW-Nonce': String(antwoord.nonce),
      'X-TW-Attest': String(antwoord.attest),
    };
  } catch (_) {
    return {};
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export const _test = { BRUG_TIMEOUT_MS };
